import type { AccountRecord } from '@libs/data-access/entities'
import { CurrencyEuroCircleIcon, IconWrap, Tooltip } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import CardInfo from '@spa/scenes/Plannings/components/Card/CardInfo/CardInfo'
import type { LabelModel } from '@spa/scenes/Plannings/redux/models/Label'
import type { RestModel } from '@spa/scenes/Plannings/redux/models/Rest'
import type { ShiftModel } from '@spa/scenes/Plannings/redux/models/Shift'
import { msToMinute, timeBetween } from '@spa/utils/time'
import React from 'react'
import type { FC } from 'react'
import getPlannedBreakDuration from 'snap-redux/modules/shifts/utils/breaks/getPlannedBreakDuration'
import getRealBreakDuration from 'snap-redux/modules/shifts/utils/breaks/getRealBreakDuration'

import minuteToDiff from '../utils/minuteToDiff'

type Props = {
  shift: ShiftModel
  rest: RestModel
  account: AccountRecord
  label?: LabelModel
}
const ShiftTimeDifference: FC<Props> = ({ shift, rest, account, label }) => {
  const { t } = usePlanningTranslation()

  if (shift?.planification_type === 'unscheduled') {
    return null
  }

  let minutes = 0
  const deductBreaks = !account.paid_breaks

  if (shift.did_not_show && rest) {
    const start = new Date(shift.starts_at)
    const end = new Date(shift.ends_at)

    // rest duration
    minutes = msToMinute(timeBetween(start, end))

    // if account.paid_leave is false
    // we deduct planned break from rest duration
    // because rest duration equals original shift duration
    if (deductBreaks) {
      const breaksDuration = getPlannedBreakDuration(shift.shift_breaks)
      // we do not deduct break duration here but add it
      // because if our variable minute is negative (minute = -60 = -1h)
      // so we add our minute to the rest duration
      minutes += breaksDuration
    }
  } else {
    // we place `ends` before `start` because we want to know if diff is positive or negative
    // in minute
    const time = {
      planned: msToMinute(
        timeBetween(new Date(shift.ends_at), new Date(shift.starts_at))
      ),
      real: msToMinute(
        timeBetween(
          new Date(shift.real_ends_at),
          new Date(shift.real_starts_at)
        )
      )
    }

    // in minute
    const breaksDuration = {
      planned: getPlannedBreakDuration(shift.shift_breaks),
      real: getRealBreakDuration(shift.shift_breaks)
    }

    minutes = time.real - time.planned

    // deduct break only if there is a difference between real break / planned break
    if (deductBreaks && breaksDuration.planned !== breaksDuration.real) {
      minutes -= breaksDuration.real - breaksDuration.planned
    }
  }

  if (!rest && !shift.did_not_show && minutes === 0) {
    return null
  }

  return (
    <CardInfo.RightContent color={label?.color}>
      {minuteToDiff(minutes)}
      {!shift.did_not_show && shift.witheld_from_pay && (
        <Tooltip
          id='shift-witheld-from-pay'
          label={t('weekly.card.salaryDeduction')}
        >
          <IconWrap
            icon={<CurrencyEuroCircleIcon />}
            size='s'
            className='mt-1'
          />
        </Tooltip>
      )}
    </CardInfo.RightContent>
  )
}

export default ShiftTimeDifference
